import styles from './CloverGooglePayButton.module.scss';

import clsx from 'clsx';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { GOOGLE_PAY_BUTTON_TEST_ID } from 'routes/Checkout/const';
import { useSubmitOrder } from '../context/SubmitOrder/SubmitOrder';
import { useMerchant } from 'hooks/context/merchant';
import { useOrderTotals } from '../context/useOrderTotals';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useCheckoutValidity } from '../context/CheckoutValidity';
import { useDeliveryPlacementConditions } from 'hooks/useDeliveryPlacementConditions';
import { useGetUnavailableItems } from 'hooks/useGetUnavailableItems';

type CloverPaymentInputProps = {
  className?: string;
  cloverElementsInstance?: Clover.ElementRoot;
}

const GOOGLE_PAY_PAYMENT_METHOD = 'GOOGLE_PAY';

export const CloverGooglePayButton: FC<CloverPaymentInputProps> = ({ cloverElementsInstance, ...props }) => {
  const ref = useRef<HTMLDivElement>(null);
  const elementRef = useRef<Clover.InputElement>();
  const [disableGpay, setDisableGpay]= useState<boolean>(false);
  const { isOrderProcessing, submitWithGooglePay, isReCaptchaReady,hasUnrecoverableError } = useSubmitOrder();
  const { isLoading: isMerchantLoading, merchant } = useMerchant();
  const { isCheckoutValid } = useCheckoutValidity();
  const { isAcceptingOrders } = useMerchantState();
  const { disableDeliverySubmit } = useDeliveryPlacementConditions();
  const { hasUnavailableItems } = useGetUnavailableItems();
  const { totalWithTip } = useOrderTotals();
  const { subtotal } = useOrderTotals();

  const isEnabled = useMemo(() => merchant?.paymentMethods.includes(GOOGLE_PAY_PAYMENT_METHOD), [merchant]);

  const gPayRequest: Clover.GPayRequest = useMemo(() => ({
    total: {
      label: `${merchant?.name} Clover Online Order`,
      amount: subtotal * 100,
    },
    currency: 'USD',
    country: 'US'
  }), [subtotal, merchant]);

  useEffect(() => {
    if (!isReCaptchaReady || !isEnabled || !cloverElementsInstance || !ref.current) return;

    if (!elementRef.current) {
      elementRef.current = cloverElementsInstance.create('PAYMENT_REQUEST_BUTTON', { paymentReqData: gPayRequest });
    }
    elementRef.current.mountNode(ref.current);

    // TODO: COLO-913 blocked by ECOMM-3082
    elementRef.current.addEventListener('paymentMethod', submitWithGooglePay);

    return () => {
      elementRef.current?.removeEventListener('paymentMethod');
    };

  }, [isReCaptchaReady, isEnabled, cloverElementsInstance, gPayRequest, submitWithGooglePay]);

  useEffect(() => {

    if (isOrderProcessing || isMerchantLoading
      || !isAcceptingOrders || hasUnrecoverableError || disableDeliverySubmit || hasUnavailableItems || totalWithTip <= 0 || (!isCheckoutValid && totalWithTip > 0) ){
        setDisableGpay(true);
        if (elementRef && elementRef.current && elementRef.current.mountedIFrame) {
          elementRef.current.mountedIFrame.tabIndex = -1;
        }
      } else {
        setDisableGpay(false);
        if (elementRef && elementRef.current && elementRef.current.mountedIFrame) {
          elementRef.current.mountedIFrame.removeAttribute('tabIndex');
        }
      }

  },[isOrderProcessing ,isMerchantLoading,isAcceptingOrders ,hasUnrecoverableError , disableDeliverySubmit ,hasUnavailableItems ,isCheckoutValid , totalWithTip]);

  return ( isEnabled ?
    <div
      {...props}
      ref={ref}
      className={clsx({
         [styles.container] : true ,
         [styles.containerWithDisableIframe]: disableGpay })}
      data-testid={GOOGLE_PAY_BUTTON_TEST_ID.root}
    /> : null
  );
};
